import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PLATFORM_ROLE_STYLES = {
  superadmin: { label: 'Superadmin', className: 'bg-purple-100 text-purple-700 border border-purple-200', icon: 'Shield' },
  user: { label: 'Standard', className: 'bg-gray-100 text-gray-600 border border-gray-200', icon: 'User' },
};

const ORG_ROLE_STYLES = {
  owner: { label: 'Owner', className: 'bg-yellow-100 text-yellow-700 border border-yellow-200', icon: 'Crown' },
  admin: { label: 'Admin', className: 'bg-blue-100 text-blue-700 border border-blue-200', icon: 'ShieldCheck' },
  manager: { label: 'Manager', className: 'bg-green-100 text-green-700 border border-green-200', icon: 'Briefcase' },
  technician: { label: 'Technician', className: 'bg-orange-100 text-orange-700 border border-orange-200', icon: 'Wrench' },
  salesperson: { label: 'Sales', className: 'bg-pink-100 text-pink-700 border border-pink-200', icon: 'TrendingUp' },
  sales: { label: 'Sales', className: 'bg-pink-100 text-pink-700 border border-pink-200', icon: 'TrendingUp' },
  viewer: { label: 'Viewer', className: 'bg-gray-100 text-gray-500 border border-gray-200', icon: 'Eye' },
};

const RoleBadge = ({ type, value }) => {
  const styles = type === 'platform' ? PLATFORM_ROLE_STYLES : ORG_ROLE_STYLES;
  const config = styles?.[value];
  if (!config || !value) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config?.className}`}>
      <Icon name={config?.icon} size={10} color="currentColor" />
      {config?.label}
    </span>
  );
};

const PersonnelCard = ({ person, onViewProfile, roleType, isSelected, onSelect, selectionMode, onSendInvite, onAssignRole }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'bg-success/10 text-success';
      case 'On Job': return 'bg-warning/10 text-warning';
      case 'Off Duty': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'technician': return 'Wrench';
      case 'manager': return 'Briefcase';
      case 'sales': return 'TrendingUp';
      default: return 'User';
    }
  };

  const hasPlatformRole = !!person?.platformRole;
  const hasOrgRole = !!person?.orgRole;
  const showRoleBadges = hasPlatformRole || hasOrgRole;

  return (
    <div className={`bg-card rounded-lg border p-4 md:p-6 hover:shadow-elevation-2 transition-smooth ${
      isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'
    }`}>
      <div className="flex flex-col sm:flex-row items-start gap-4">
        {selectionMode && (
          <div className="flex items-start pt-1">
            <Checkbox
              checked={isSelected}
              onChange={(e) => onSelect?.(person?.id, e?.target?.checked)}
            />
          </div>
        )}
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden flex-shrink-0 bg-muted">
          <Image
            src={person?.avatar}
            alt={person?.avatarAlt}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex-1 min-w-0 w-full">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-1 truncate">
                {person?.name}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Icon name={getRoleIcon(roleType)} size={16} color="currentColor" />
                <span className="truncate">{person?.role}</span>
              </div>
              {/* Role Badges */}
              {showRoleBadges && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {hasPlatformRole && (
                    <RoleBadge type="platform" value={person?.platformRole} />
                  )}
                  {hasOrgRole && (
                    <RoleBadge type="org" value={person?.orgRole} />
                  )}
                </div>
              )}
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(person?.status)}`}>
              {person?.status}
            </span>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
              <span className="truncate">{person?.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Phone" size={16} color="var(--color-muted-foreground)" />
              <span>{person?.phone}</span>
            </div>
            {person?.location && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
                <span className="truncate">{person?.location}</span>
              </div>
            )}
          </div>

          {roleType === 'technician' && person?.skills && (
            <div className="flex flex-wrap gap-2 mb-4">
              {person?.skills?.slice(0, 3)?.map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
                >
                  {skill}
                </span>
              ))}
              {person?.skills?.length > 3 && (
                <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                  +{person?.skills?.length - 3} more
                </span>
              )}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={() => onViewProfile(person)}
            iconName="Eye"
            iconPosition="left"
          >
            View Profile
          </Button>
          {onSendInvite && (
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={() => onSendInvite(person)}
              iconName="Mail"
              iconPosition="left"
              className="mt-2 text-primary hover:bg-primary/10"
            >
              Send Login Invite
            </Button>
          )}
          {onAssignRole && (
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={() => onAssignRole(person)}
              iconName="ShieldCheck"
              iconPosition="left"
              className="mt-2 text-purple-600 hover:bg-purple-50"
            >
              Assign Role
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonnelCard;